import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AlertCircle, CheckCircle, UploadCloud, ArrowLeft } from "lucide-react";
import PlatformCalendar from "../../../components/shared_components/platformCalendar";
import CandidateJobDetails from "./CandidateJobDetails";
import { numberToWords } from "./ctcWords";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;
const NONE_OF_THE_ABOVE = "None of the above";
const RESUME_ALLOWED_EXT = [".pdf", ".doc", ".docx"];
const RESUME_MAX_BYTES = 3 * 1024 * 1024;

const INPUT_CLS =
    "w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 text-gray-700 focus:outline-none focus:border-[#C1A0D0] transition-colors";

// Local (not UTC) YYYY-MM-DD — avoids the day-shift toISOString() causes near midnight.
const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const fromDateStr = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
};

// 0 and false are real answers, not blanks — only "never answered" / "cleared" counts.
const isBlank = (value) => {
    if (value === undefined || value === null) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    return false;
};

// Top branding bar — matches the ATS PublicJobLayout header. title/subtitle (job role +
// company) are only passed on page 1, where JobDescriptionViewer shows them in its header too.
const BrandHeader = ({ title, subtitle }) => (
    <div className="bg-[#FAFAFA] px-4 sm:px-8 py-4 sm:py-5 shadow-sm">
        <div className="flex items-center gap-4">
            <div className="w-[70px] h-[48px] sm:w-[110px] sm:h-[60px] flex-shrink-0 flex items-center justify-center overflow-hidden rounded-xl">
                <img
                    src="/Homepagelogo.png"
                    alt="GenieHire"
                    className="max-w-full max-h-full object-contain rounded-sm"
                />
            </div>
            {title && (
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{title}</h1>
                    {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
                </div>
            )}
        </div>
    </div>
);

const PoweredBy = () => (
    <div className="flex justify-center items-center mt-6">
        <span className="text-sm text-gray-500">Powered by GenieHire</span>
    </div>
);

const ScreeningFormPage = () => {
    const { token } = useParams();
    // SANDBOX: only ever set by Screening Lab's "Generate test link" preview picker.
    const [searchParams] = useSearchParams();
    const previewJob = searchParams.get("preview_job");

    const [form, setForm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [view, setView] = useState("description"); // "description" (job info) | "questions"
    const [confirmDecline, setConfirmDecline] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [alreadyDone, setAlreadyDone] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState([]);
    const [immediateJoin, setImmediateJoin] = useState(false);
    const [dateOpen, setDateOpen] = useState(false);
    const [resumeMeta, setResumeMeta] = useState({});
    const [dragOverKey, setDragOverKey] = useState(null);
    const dateRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${SOURCING_URL}/public/screening/${token}`, {
                    params: previewJob ? { preview_job: previewJob } : {},
                });
                if (cancelled) return;
                setForm(data);
                setAlreadyDone(!!data.already_submitted);
            } catch (e) {
                if (cancelled) return;
                const detail = e.response?.data?.detail;
                setError(typeof detail === "string" ? detail : "This screening link is not valid.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token, previewJob]);

    // Fire-and-forget "opened" beacon once the page renders in a real browser. Kept off the
    // data GET so email/link-preview prefetchers (which fetch without running JS) don't
    // inflate opens. Idempotent server-side (only sets opened_at when it's still null).
    useEffect(() => {
        axios.post(`${SOURCING_URL}/public/screening/${token}/viewed`).catch(() => {});
    }, [token]);

    useEffect(() => {
        if (!dateOpen) return;
        const onClick = (e) => { if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [dateOpen]);

    const setAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));

    const handleResumeSelect = async (key, file) => {
        if (!file) return;
        const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase() : "";
        if (!RESUME_ALLOWED_EXT.includes(ext)) {
            setResumeMeta(prev => ({ ...prev, [key]: { name: file.name, status: "error", error: "File must be a PDF, DOC, or DOCX." } }));
            return;
        }
        if (file.size > RESUME_MAX_BYTES) {
            setResumeMeta(prev => ({ ...prev, [key]: { name: file.name, status: "error", error: "File must be under 3MB." } }));
            return;
        }
        setResumeMeta(prev => ({ ...prev, [key]: { name: file.name, status: "uploading", progress: 0 } }));
        try {
            const formData = new FormData();
            formData.append("file", file);
            const { data } = await axios.post(`${SOURCING_URL}/public/screening/${token}/resume`, formData, {
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    const progress = Math.round((evt.loaded * 100) / evt.total);
                    setResumeMeta(prev => ({ ...prev, [key]: { name: file.name, status: "uploading", progress } }));
                },
            });
            setAnswer(key, data.resume_url);
            setResumeMeta(prev => ({ ...prev, [key]: { name: file.name, status: "done" } }));
        } catch (e) {
            const detail = e.response?.data?.detail;
            setResumeMeta(prev => ({ ...prev, [key]: { name: file.name, status: "error", error: typeof detail === "string" ? detail : "Upload failed. Please try again." } }));
        }
    };

    const handleResumeDrop = (key, e) => {
        e.preventDefault();
        setDragOverKey(prev => (prev === key ? null : prev));
        handleResumeSelect(key, e.dataTransfer.files?.[0]);
    };

    const postAnswers = async (payload) => {
        setFieldErrors([]);
        setSubmitting(true);
        setError(null);
        try {
            await axios.post(`${SOURCING_URL}/public/screening/${token}`, { answers: payload });
            setSubmitted(true);
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.fields) {
                setFieldErrors(detail.fields);
                setError(detail.message || "Please answer the required questions.");
            } else if (e.response?.status === 409) {
                setAlreadyDone(true);
            } else {
                setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Page 1 gate: "I'm interested" carries the interested=true intent into the answers and
    // advances to the questions; "Not interested" submits interested=false straight away.
    const handleInterested = () => {
        setAnswer("interested", true);
        setError(null);
        setView("questions");
    };
    const handleDecline = () => postAnswers({ interested: false });
    const handleSubmit = () => postAnswers({ ...answers, interested: true });

    const renderField = (q) => {
        const raw = answers[q.key];
        const val = raw ?? "";
        const answered = raw !== undefined;
        switch (q.type) {
            case "boolean":
                return (
                    <div className="flex gap-3">
                        {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map(opt => (
                            <button
                                key={opt.l}
                                type="button"
                                onClick={() => setAnswer(q.key, opt.v)}
                                className={`px-6 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                                    val === opt.v
                                        ? "border-[#1569c4] bg-[#1569c4]/10 text-[#1569c4]"
                                        : "border-gray-400 text-gray-600 hover:border-gray-500"
                                }`}
                            >
                                {opt.l}
                            </button>
                        ))}
                    </div>
                );
            case "number": {
                if (q.key === "notice_period_days") {
                    return (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setImmediateJoin(true);
                                        setAnswer("notice_period_days", 0);
                                    }}
                                    className={`px-5 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                                        immediateJoin
                                            ? "border-[#1569c4] bg-[#1569c4]/10 text-[#1569c4]"
                                            : "border-gray-400 text-gray-600 hover:border-gray-500"
                                    }`}
                                >
                                    Immediate joining
                                </button>
                                <span className="text-gray-400 text-xs">or</span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={1}
                                        inputMode="numeric"
                                        value={immediateJoin ? "" : (answers.notice_period_days ?? "")}
                                        onChange={e => {
                                            setImmediateJoin(false);
                                            // Switching back to manual entry must drop any earlier
                                            // "immediate join" date — otherwise it lingers in `answers`
                                            // and gets submitted alongside the real day count.
                                            setAnswer("available_from", undefined);
                                            setAnswer("notice_period_days", e.target.value);
                                        }}
                                        placeholder="0"
                                        className="w-24 px-3 py-2.5 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 text-gray-700 focus:outline-none focus:border-[#C1A0D0] transition-colors text-center"
                                    />
                                    <span className="text-sm text-gray-500">days notice</span>
                                </div>
                            </div>
                            {immediateJoin && (
                                <div>
                                    <p className="text-sm font-medium text-gray-600 mb-2">
                                        Nearest date you can join <span className="text-red-500">*</span>
                                    </p>
                                    <div className="relative w-fit" ref={dateRef}>
                                        <button
                                            type="button"
                                            onClick={() => setDateOpen(o => !o)}
                                            className={`${INPUT_CLS} min-w-[200px] text-left`}
                                        >
                                            <span className={answers.available_from ? "" : "text-gray-400"}>
                                                {answers.available_from
                                                    ? fromDateStr(answers.available_from).toLocaleDateString()
                                                    : "Select a date"}
                                            </span>
                                        </button>
                                        {dateOpen && (
                                            <div
                                                className="absolute z-20 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg p-1"
                                                style={{ width: 300 }}
                                            >
                                                <PlatformCalendar
                                                    className="light"
                                                    value={fromDateStr(answers.available_from)}
                                                    minDate={new Date()}
                                                    onChange={(date) => {
                                                        setAnswer("available_from", toDateStr(date));
                                                        setDateOpen(false);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }
                // CTC questions are money → a currency-aware words helper reflects the exact
                // digits typed, so magnitude ("12" vs "1200000") can't be misread. Absolute figure.
                const money = q.key === "expected_ctc" || q.key === "current_ctc";
                const currency = form?.currency;
                const words = money ? numberToWords(val, currency) : "";
                return (
                    <div>
                        <input
                            type="number"
                            min={0}
                            inputMode="decimal"
                            value={val}
                            onChange={e => setAnswer(q.key, e.target.value)}
                            className={INPUT_CLS}
                            placeholder={money ? (currency ? `Annual amount in ${currency}, e.g. 1200000` : "Annual amount, e.g. 1200000") : ""}
                        />
                        {money && (
                            words
                                ? <p className="text-xs text-[#1569c4] mt-1">{words}</p>
                                : <p className="text-xs text-gray-400 mt-1">Enter the full yearly figure{currency ? ` in ${currency}` : ""}, numbers only.</p>
                        )}
                    </div>
                );
            }
            case "date":
                return <input type="date" value={val} onChange={e => setAnswer(q.key, e.target.value)} className={INPUT_CLS} />;
            case "select":
                return (
                    <select value={val} onChange={e => setAnswer(q.key, e.target.value)} className={`${INPUT_CLS} bg-white`}>
                        <option value="">Select…</option>
                        {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                );
            case "multiselect": {
                // Answer is always an array. One option → Yes/No; many → checkboxes; none → omit.
                const opts = q.options || [];
                const arr = Array.isArray(raw) ? raw : [];
                if (opts.length === 0) return null;
                if (opts.length === 1) {
                    const only = opts[0];
                    const yes = arr.includes(only);
                    return (
                        <div className="flex gap-3">
                            {[{ on: true, l: "Yes" }, { on: false, l: "No" }].map(o => {
                                const sel = answered && (o.on ? yes : !yes);
                                return (
                                    <button
                                        key={o.l}
                                        type="button"
                                        onClick={() => setAnswer(q.key, o.on ? [only] : [])}
                                        className={`px-6 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                                            sel
                                                ? "border-[#1569c4] bg-[#1569c4]/10 text-[#1569c4]"
                                                : "border-gray-400 text-gray-600 hover:border-gray-500"
                                        }`}
                                    >
                                        {o.l}
                                    </button>
                                );
                            })}
                        </div>
                    );
                }
                const isLocations = q.key === "open_to_locations";
                const toggle = (o) => {
                    if (!isLocations) return setAnswer(q.key, arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
                    if (o === NONE_OF_THE_ABOVE) return setAnswer(q.key, arr.includes(o) ? [] : [NONE_OF_THE_ABOVE]);
                    const without = arr.filter(x => x !== NONE_OF_THE_ABOVE);
                    return setAnswer(q.key, without.includes(o) ? without.filter(x => x !== o) : [...without, o]);
                };
                return (
                    <div className="flex flex-col gap-2">
                        {opts.map(o => {
                            const on = arr.includes(o);
                            return (
                                <label key={o} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={() => toggle(o)}
                                        className="accent-[#1569c4] w-4 h-4"
                                    />
                                    {o}
                                </label>
                            );
                        })}
                    </div>
                );
            }
            case "file": {
                const meta = resumeMeta[q.key];
                const uploaded = !!val;
                const isDragOver = dragOverKey === q.key;
                return (
                    <div>
                        <input
                            type="file"
                            accept={RESUME_ALLOWED_EXT.join(",")}
                            id={`file-${q.key}`}
                            className="hidden"
                            onChange={e => handleResumeSelect(q.key, e.target.files?.[0])}
                        />
                        <div
                            onDrop={e => handleResumeDrop(q.key, e)}
                            onDragOver={e => { e.preventDefault(); setDragOverKey(q.key); }}
                            onDragLeave={e => { e.preventDefault(); setDragOverKey(prev => (prev === q.key ? null : prev)); }}
                            className={`border-2 border-dashed rounded-lg transition-colors ${
                                meta?.status === "error"
                                    ? "border-red-400"
                                    : uploaded
                                    ? "border-green-500 bg-green-500/10"
                                    : isDragOver
                                    ? "border-[#1569c4] bg-[#1569c4]/5"
                                    : "border-gray-400 hover:border-gray-500"
                            }`}
                        >
                            <label
                                htmlFor={`file-${q.key}`}
                                className="flex flex-col items-center justify-center gap-1.5 px-4 py-8 cursor-pointer text-center"
                            >
                                {meta?.status === "uploading" ? (
                                    <>
                                        <UploadCloud className="w-8 h-8 text-gray-400 animate-pulse" />
                                        <span className="text-sm text-gray-600 truncate max-w-full">Uploading {meta.name}…</span>
                                        <div className="w-full max-w-[220px] bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className="h-1.5 rounded-full transition-all duration-200"
                                                style={{ width: `${meta.progress || 0}%`, background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                            />
                                        </div>
                                        <span className="text-xs text-gray-400">{meta.progress || 0}%</span>
                                    </>
                                ) : uploaded ? (
                                    <>
                                        <CheckCircle className="w-8 h-8 text-green-500" />
                                        <span className="text-sm font-medium text-gray-700 truncate max-w-full">{meta?.name || "Resume uploaded"}</span>
                                        <span className="text-xs font-medium text-[#1569c4]">Change file</span>
                                    </>
                                ) : (
                                    <>
                                        <UploadCloud className="w-8 h-8 text-gray-400" />
                                        <span className="text-sm text-gray-600">
                                            <span className="font-medium text-[#1569c4]">Click to upload</span> or drag and drop
                                        </span>
                                        <span className="text-xs text-gray-400">PDF, DOC, DOCX — max 3MB</span>
                                    </>
                                )}
                            </label>
                        </div>
                        {meta?.status === "error" && <p className="text-xs text-red-500 mt-1">{meta.error}</p>}
                    </div>
                );
            }
            default:
                return (
                    <div>
                        <input type="text" maxLength={500} value={val} onChange={e => setAnswer(q.key, e.target.value)} className={INPUT_CLS} />
                        <p className="mt-1 text-xs text-gray-400 text-right">{String(val).length}/500</p>
                    </div>
                );
        }
    };

    // Page shell — light ATS background + branded header.
    const page = (children) => (
        <div className="min-h-screen bg-[#F1F4FB]">
            <BrandHeader />
            <div className="px-4 sm:px-6 lg:px-8 py-8 lg:py-12 flex justify-center">
                <div className="w-full max-w-2xl">{children}</div>
            </div>
        </div>
    );

    const card = (children) => (
        <div className="rounded-3xl p-8 shadow-sm bg-white">{children}</div>
    );

    // Wider shell — room for the full job-details layout (page 1) and the sticky
    // job-summary sidebar (page 2). headerProps forwards title/subtitle to BrandHeader.
    const pageWide = (children, headerProps = {}) => (
        <div className="min-h-screen bg-[#F1F4FB]">
            <BrandHeader {...headerProps} />
            <div className="px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                <div className="w-full">{children}</div>
            </div>
        </div>
    );

    if (loading) {
        return page(
            <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1569c4]" />
            </div>
        );
    }

    if (error && !form) {
        return page(card(
            <div className="text-center py-6">
                <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-medium text-gray-700 mb-2">Invalid Link</h2>
                <p className="text-gray-500 text-sm">{error}</p>
            </div>
        ));
    }

    if (submitted || alreadyDone) {
        return page(card(
            <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h2 className="text-3xl font-medium text-gray-700 mb-3">Thank You!</h2>
                <p className="text-gray-600">
                    {alreadyDone && !submitted
                        ? "We've already received your response. Our recruiter will reach out to you soon."
                        : "Your response has been recorded. Our recruiter will reach out to you soon."}
                </p>
                <PoweredBy />
            </div>
        ));
    }

    // ── Page 1 — job details (from the enrollment's linked job, read live from Mongo) +
    // interest gate. No linked job → CandidateJobDetails renders its own compact fallback. ──
    if (view === "description") {
        return pageWide(
            <>
                <CandidateJobDetails
                    job={form.job}
                    formName={form.form_name}
                    candidateFirstName={form.candidate_first_name}
                    onApply={handleInterested}
                    onDecline={() => {
                        if (!submitting && window.confirm("Are you sure you're not interested in this opportunity?")) handleDecline();
                    }}
                    applyBusy={submitting}
                    applyError={error}
                />
                <PoweredBy />
            </>,
            form.job ? { title: form.job.job_role, subtitle: form.job.company_name } : {}
        );
    }

    // ── Page 2 — screening questions ─────────────────────────────────────────
    const resumeUploading = Object.values(resumeMeta).some(m => m.status === "uploading");

    // Resume is always shown first (right after the interested gate), regardless of the
    // order the recruiter's builder happened to save the questions array in.
    const rawQuestions = form.questions || [];
    const resumeQ = rawQuestions.find(q => q.key === "resume_upload");
    const orderedQuestions = resumeQ
        ? (() => {
            const rest = rawQuestions.filter(q => q.key !== "resume_upload");
            const insertAt = rest.findIndex(q => q.key === "interested") + 1;
            const next = [...rest];
            next.splice(insertAt, 0, resumeQ);
            return next;
        })()
        : rawQuestions;

    const hasNoticePeriod = orderedQuestions.some(fq => fq.key === "notice_period_days");
    // The interested question is now the page-1 CTA, so it's dropped here; available_from is
    // shown inline under notice_period_days when "immediate" is picked.
    const formQuestions = orderedQuestions.filter(q => {
        if (q.key === "interested") return false;
        if (q.key === "available_from" && hasNoticePeriod) return false;
        return true;
    });
    // Mirrors the server's missing-required check so Submit is disabled before the click.
    // "Immediate joining" reveals a required earliest-join date that isn't a form question,
    // so enforce it here (the server can't tell 0-day notice from immediate-with-no-date).
    const hasMissingRequired =
        (immediateJoin && isBlank(answers.available_from)) ||
        formQuestions.some(q => q.required && isBlank(answers[q.key]));

    return pageWide(
        <>
            <button
                onClick={() => { setView("description"); setError(null); }}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors mb-4"
            >
                <ArrowLeft className="w-4 h-4" /> Back to role details
            </button>

            {card(
                <>
                    <h3 className="text-xl font-medium text-gray-700 mb-1">
                        {form.form_name || "A few quick questions"}
                    </h3>
                    {form.candidate_first_name && (
                        <p className="text-sm text-gray-500 mb-1">
                            Hi {form.candidate_first_name}, just a few details to finish.
                        </p>
                    )}

                    <div className="mt-6 space-y-5">
                        {formQuestions.map(q => (
                            <div key={q.key}>
                                <label className="block text-sm font-medium text-gray-600 mb-2">
                                    {q.label}
                                    {q.required && <span className="text-red-500 ml-0.5">*</span>}
                                </label>
                                {renderField(q)}
                                {(fieldErrors.includes(q.label) || fieldErrors.includes(q.key)) && (
                                    <p className="text-xs text-red-500 mt-1">This field is required.</p>
                                )}
                            </div>
                        ))}
                    </div>

                    {error && <p className="text-sm text-red-500 mt-5">{error}</p>}

                    <div className="w-full flex items-center justify-center pt-7">
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || resumeUploading || hasMissingRequired}
                            title={hasMissingRequired ? "Please answer all required questions first" : undefined}
                            className="w-[40%] min-w-[160px] py-3 rounded-full text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                        >
                            {submitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </>
            )}
            <PoweredBy />
        </>,
        form.job ? { title: form.job.job_role, subtitle: form.job.company_name } : {}
    );
};

export default ScreeningFormPage;